import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Sparkles, Bot, User as UserIcon, Plus, MessageSquare, Trash2, History, LayoutDashboard, MessageCircle, Pencil, ThumbsUp, ThumbsDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import AdvisorDashboard from "@/pages/advisor/AdvisorDashboard";

const SUGGESTIONS = [
  "How do I choose between an LLC and an S-Corp?",
  "What are the first steps to build business credit?",
  "What compliance filings am I missing right now?",
  "How do I prepare my business for funding?",
];

const newKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `m_${Date.now()}_${Math.random()}`;

function MessageBubble({ role, content, onFeedback }) {
  const isUser = role === "user";
  const [rated, setRated] = useState(null);
  const rate = (r) => { setRated(r); onFeedback?.(r); };
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${role}`}>
      <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${isUser ? "bg-white/10" : "fv-gold-gradient"}`}>
        {isUser ? <UserIcon className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-[#0A0A0B]" />}
      </div>
      <div className={`max-w-[80%] ${isUser ? "" : "space-y-1.5"}`}>
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-[#D4AF37]/15 text-white" : "bg-[#1C1C1F] text-[#E4E4E7]"}`}>
          {content}
        </div>
        {!isUser && onFeedback && (
          <div className="flex items-center gap-2 pl-1" data-testid="ai-feedback">
            <button onClick={() => rate("helpful")} data-testid="feedback-helpful" className={`transition-colors ${rated === "helpful" ? "text-emerald-400" : "text-[#52525B] hover:text-[#A1A1AA]"}`}><ThumbsUp className="w-3.5 h-3.5" /></button>
            <button onClick={() => rate("not_helpful")} data-testid="feedback-not-helpful" className={`transition-colors ${rated === "not_helpful" ? "text-red-400" : "text-[#52525B] hover:text-[#A1A1AA]"}`}><ThumbsDown className="w-3.5 h-3.5" /></button>
            <button onClick={() => rate("incorrect")} data-testid="feedback-incorrect" className={`text-[11px] transition-colors ${rated === "incorrect" ? "text-red-400" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>Incorrect</button>
            <button onClick={() => rate("needs_detail")} data-testid="feedback-needs-detail" className={`text-[11px] transition-colors ${rated === "needs_detail" ? "text-[#D4AF37]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>Needs detail</button>
            {rated && <span className="text-[11px] text-[#52525B]">Thanks!</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-lg fv-gold-gradient flex items-center justify-center"><Bot className="w-4 h-4 text-[#0A0A0B]" /></div>
      <div className="bg-[#1C1C1F] rounded-2xl px-4 py-3.5 flex gap-1.5">
        {[0, 1, 2].map((d) => <span key={d} className="w-2 h-2 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
      </div>
    </div>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-10">
      <Bot className="w-12 h-12 text-[#D4AF37] mb-4" />
      <h3 className="font-heading text-2xl text-white mb-2">How can I help you grow?</h3>
      <p className="text-[#71717A] mb-7 max-w-md">Ask anything about launching, funding, or managing your business.</p>
      <div className="grid sm:grid-cols-2 gap-3 w-full max-w-2xl">
        {SUGGESTIONS.map((s) => (
          <button key={s} onClick={() => onPick(s)} data-testid="ai-suggestion"
            className="text-left text-sm text-[#D4D4D8] border border-white/10 rounded-xl px-4 py-3 hover:border-[#D4AF37]/40 hover:bg-white/5 transition-colors">
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ConversationList({ conversations, activeId, onSelect, onNew, onDelete, onRename }) {
  const [q, setQ] = useState("");
  const filtered = conversations.filter((c) => c.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="flex flex-col h-full">
      <Button onClick={onNew} data-testid="ai-new-chat-btn"
        className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90 mb-3">
        <Plus className="w-4 h-4 mr-1" /> New chat
      </Button>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search conversations…" data-testid="ai-conversation-search"
        className="mb-3 w-full bg-[#121214] border border-white/10 rounded-lg px-3 h-9 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/40" />
      <div className="flex-1 overflow-y-auto space-y-1.5" data-testid="ai-conversations">
        {filtered.length === 0 && <p className="text-xs text-[#52525B] px-2 py-3">No conversations found.</p>}
        {filtered.map((c) => (
          <div key={c.conversation_id}
            className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
              activeId === c.conversation_id ? "bg-[#D4AF37]/12 border border-[#D4AF37]/30" : "hover:bg-white/5 border border-transparent"}`}
            onClick={() => onSelect(c.conversation_id)} data-testid={`conversation-${c.conversation_id}`}>
            <MessageSquare className="w-4 h-4 text-[#71717A] shrink-0" />
            <span className="text-sm text-[#D4D4D8] truncate flex-1">{c.title}</span>
            <button onClick={(e) => { e.stopPropagation(); onRename(c); }} data-testid={`rename-conversation-${c.conversation_id}`}
              className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-[#D4AF37] transition-opacity"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(c.conversation_id); }}
              data-testid={`delete-conversation-${c.conversation_id}`}
              className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-red-400 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AIAssistant() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState("advisor");
  const scrollRef = useRef(null);

  const loadConversations = useCallback(() => api.get("/ai/conversations").then(({ data }) => setConversations(data)).catch(() => {}), []);
  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const selectConversation = useCallback(async (id) => {
    setActiveId(id);
    try {
      const { data } = await api.get(`/ai/conversations/${id}/messages`);
      setMessages(data.map((m) => ({ ...m, _key: newKey() })));
    } catch { setMessages([]); }
  }, []);

  const newChat = useCallback(() => { setActiveId(null); setMessages([]); }, []);

  const deleteConversation = useCallback(async (id) => {
    try {
      await api.delete(`/ai/conversations/${id}`);
      if (activeId === id) newChat();
      loadConversations();
      toast.success("Conversation deleted");
    } catch { toast.error("Could not delete conversation"); }
  }, [activeId, newChat, loadConversations]);

  const renameConversation = useCallback(async (c) => {
    const title = window.prompt("Rename conversation", c.title);
    if (!title || !title.trim()) return;
    try { await api.patch(`/ai/conversations/${c.conversation_id}`, { title: title.trim() }); loadConversations(); toast.success("Renamed"); }
    catch { toast.error("Could not rename"); }
  }, [loadConversations]);

  const sendFeedback = useCallback(async (index, content, rating) => {
    if (!activeId) return;
    try { await api.post("/advisor/feedback", { conversation_id: activeId, message_index: index, content, rating }); }
    catch { /* non-blocking */ }
  }, [activeId]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { _key: newKey(), role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, conversation_id: activeId });
      setMessages((m) => [...m, { _key: newKey(), role: "assistant", content: data.reply }]);
      if (!activeId) setActiveId(data.conversation_id);
      loadConversations();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI is unavailable");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }, [input, loading, activeId, loadConversations]);

  return (
    <div className="fv-fade-up h-[calc(100vh-8rem)] flex gap-5">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col rounded-2xl border border-white/8 bg-[#0d0d0f] p-3">
        <ConversationList conversations={conversations} activeId={activeId} onSelect={(id) => { selectConversation(id); setTab("chat"); }} onNew={() => { newChat(); setTab("chat"); }} onDelete={deleteConversation} onRename={renameConversation} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl fv-gold-gradient flex items-center justify-center"><Sparkles className="w-5 h-5 text-[#0A0A0B]" /></div>
            <div>
              <h1 className="font-heading text-2xl text-white">AI Business Advisor</h1>
              <p className="text-[#71717A] text-sm">Powered by GPT-5.5 · Uses your real business data</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full border border-white/10 p-0.5" data-testid="advisor-tabs">
              <button onClick={() => setTab("advisor")} data-testid="tab-advisor" className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${tab === "advisor" ? "fv-gold-gradient text-[#0A0A0B] font-semibold" : "text-[#A1A1AA] hover:text-white"}`}><LayoutDashboard className="w-3.5 h-3.5" /> Advisor</button>
              <button onClick={() => setTab("chat")} data-testid="tab-chat" className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${tab === "chat" ? "fv-gold-gradient text-[#0A0A0B] font-semibold" : "text-[#A1A1AA] hover:text-white"}`}><MessageCircle className="w-3.5 h-3.5" /> Chat</button>
            </div>
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button data-testid="ai-history-mobile" variant="outline"
                  className="lg:hidden border-white/15 bg-transparent text-white hover:bg-white/5 rounded-full"><History className="w-4 h-4" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0d0d0f] border-white/10 w-72 p-4">
                <ConversationList conversations={conversations} activeId={activeId}
                  onSelect={(id) => { selectConversation(id); setDrawerOpen(false); setTab("chat"); }}
                  onNew={() => { newChat(); setDrawerOpen(false); setTab("chat"); }} onDelete={deleteConversation} onRename={renameConversation} />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {tab === "advisor" ? (
          <div className="flex-1 rounded-2xl border border-white/8 bg-[#0d0d0f] p-5 min-h-0"><AdvisorDashboard /></div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-2xl border border-white/8 bg-[#0d0d0f] p-5 space-y-5" data-testid="ai-messages">
              {messages.length === 0 && !loading && <EmptyState onPick={send} />}
              {messages.map((m, i) => <MessageBubble key={m._key} role={m.role} content={m.content} onFeedback={m.role === "assistant" ? (r) => sendFeedback(i, m.content, r) : undefined} />)}
              {loading && <TypingIndicator />}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-3">
              <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-input"
                placeholder="Ask your AI advisor…"
                className="flex-1 bg-[#121214] border border-white/10 rounded-xl px-4 h-12 text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/50" />
              <Button type="submit" disabled={loading || !input.trim()} data-testid="ai-send-btn"
                className="fv-gold-gradient text-[#0A0A0B] h-12 w-12 p-0 rounded-xl hover:opacity-90 shrink-0"><Send className="w-5 h-5" /></Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
